// Utilities
import { defineStore } from 'pinia';
import type {
  UploadProgressItem,
  UploadProgressState
} from '@/types/shared/uploadProgress';

/**
 * Below this many bytes an upload finishes fast enough that a progress card
 * would flash up and vanish, which reads as a glitch. Those upload silently.
 */
const MIN_TRACKED_BYTES = 8 * 1024 * 1024;

/**
 * Ignore the opening moments when estimating speed: the first bytes leave
 * before the connection has settled, so extrapolating from them alone gives a
 * wildly optimistic time remaining.
 */
const MIN_SAMPLE_MS = 2000;

/** How far along an upload is, 0 to 1. */
export function uploadFraction(item: UploadProgressItem): number {
  if (item.bytesTotal <= 0) return 0;
  return Math.min(1, item.bytesSent / item.bytesTotal);
}

/**
 * Seconds left at the speed measured so far, or null while that is not yet
 * worth quoting — too early to tell, or already done.
 */
export function uploadSecondsRemaining(
  item: UploadProgressItem
): number | null {
  if (item.bytesPerSecond === null || item.bytesPerSecond <= 0) return null;
  const remaining = item.bytesTotal - item.bytesSent;
  if (remaining <= 0) return null;
  return remaining / item.bytesPerSecond;
}

export const useUploadProgressStore = defineStore('uploadProgress', {
  state: (): UploadProgressState => ({
    items: []
  }),
  actions: {
    /**
     * Starts tracking an upload. Small files are skipped outright, so callers
     * can report every upload and let the store decide what deserves a card.
     */
    begin(
      id: string,
      fileName: string,
      bytesTotal: number,
      chunkCount: number
    ): void {
      if (bytesTotal < MIN_TRACKED_BYTES) return;
      this.items.push({
        id,
        fileName,
        bytesSent: 0,
        bytesTotal,
        chunkIndex: 1,
        chunkCount,
        startedAt: performance.now(),
        bytesPerSecond: null,
        cancelling: false
      });
    },
    /**
     * Records how many bytes have gone out in total. Speed is averaged over
     * the whole upload rather than the latest event, so the estimate settles
     * instead of jumping about on every packet.
     */
    report(id: string, bytesSent: number, chunkIndex: number): void {
      const item = this.items.find(entry => entry.id === id);
      if (!item) return;

      const elapsed = performance.now() - item.startedAt;
      item.bytesSent = Math.min(bytesSent, item.bytesTotal);
      item.chunkIndex = chunkIndex;
      item.bytesPerSecond =
        elapsed >= MIN_SAMPLE_MS && bytesSent > 0
          ? bytesSent / (elapsed / 1000)
          : null;
    },
    /** Flags that the user has asked to stop, while the abort takes effect. */
    markCancelling(id: string): void {
      const item = this.items.find(entry => entry.id === id);
      if (item) item.cancelling = true;
    },
    /** Stops tracking one upload, leaving any others running untouched. */
    end(id: string): void {
      this.items = this.items.filter(entry => entry.id !== id);
    }
  }
});
